import React, { useEffect, useMemo, useRef, useState } from 'react';
import HorizontalDotsButton from '@/components/HorizontalDotsButton/HorizontalDotsButton';
import CircleNumber from '@/components/CircleNumber/CircleNumber';
import Dropdown, { ListItem } from '@/components/Dropdown/Dropdown';
import {
	AI,
	BracketIcon,
	Checkmark,
	CloseThin,
	HardArrowLeft,
	PencilEdit
} from '@/resources/assets/images';
import DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import RichEditor from '@/components/RichEditor/RichEditor';
import EmailSmartTemplates from '@/modules/email/components/EmailSmartTemplates/EmailSmartTemplatesRedesign';
import { updateSequence } from '@/services/sequencesService';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { toast } from 'react-hot-toast';
import '../sequences.scss';
import { PlusSignIcon } from '@/components/SvgIcons';
import Button from '@/components/Button/newButtons';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import HeaderRedesign from '@/modules/main/components/HeaderRedesign/HeaderRedesign';
import { useHistory } from 'react-router-dom';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { cn } from '@/lib/classNames';
import type { HttpResponseSuccess } from '@/services/service';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/components/atoms/Dialog';
import {
	AI_SEQUENCE_QUERY_KEY,
	AiSequenceResponse
} from '@/modules/onboarding/hooks/useCreateAiSequence';
import pluralize from 'pluralize';
import NewSequenceFooter from '../components/NewSequenceFooter';
import { useSteps } from '../hooks/useSteps';
import type { StepsType } from '../hooks/useSequences';
import { useSequencesContext } from '../SequencesContext';
import AiSequenceDialogForm from '../components/AiModal/molecules/AiSequenceDialogForm';

export default function SequenceStepCreationPage() {
	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const history = useHistory();
	const isUserOnboarding = user?.onboarding?.status === 'pending';

	const {
		selectedStep,
		setSequencePage,
		selectedTemplate,
		selectedSequence,
		setSelectedTemplate,
		setSelectedSequence,
		setSelectedStep,
		sequencePage
	} = useSequencesContext();

	const queryClient = useQueryClient();
	const onboardingGeneratedAiSequence = queryClient.getQueryData<
		HttpResponseSuccess<AiSequenceResponse>
	>(AI_SEQUENCE_QUERY_KEY);

	const [subject, setSubject] = useState(selectedStep?.subject || '');
	const [emailBody, setEmailBody] = useState(selectedStep?.body || '');
	const [bodyContextMenuValue, setBodyContextMenuValue] = useState<string>();
	const [focusField, setFocusField] = useState<'SUBJECT' | 'BODY' | null>(
		null
	);
	const screenSize = useIsScreenWidthSmallerThan(1024);
	const inputSubjectRef = useRef<HTMLInputElement>(null);
	const [onboardingModal, setOnboardingModal] = useState<boolean>(
		!!onboardingGeneratedAiSequence?.data?.data
	);
	const [isInputsHighlighted, setIsInputsHighlighted] = useState(false);
	const [isSubjectInitial, setIsSubjectInitial] = useState<boolean>(true);
	const [isBodyInitial, setIsBodyInitial] = useState<boolean>(true);

	const clearAiSequenceCache = () => {
		queryClient.invalidateQueries({
			queryKey: AI_SEQUENCE_QUERY_KEY,
			refetchType: 'none'
		});

		queryClient.removeQueries({
			queryKey: AI_SEQUENCE_QUERY_KEY,
			exact: true,
			type: 'all'
		});
	};

	useEffect(() => {
		if (isUserOnboarding && onboardingGeneratedAiSequence?.status === 200) {
			setTimeout(() => {
				const { name, description, isAiGenerated, steps } =
					onboardingGeneratedAiSequence?.data?.data;

				const processedSteps = steps.map((step, index) => ({
					...step,
					id: `id-${index}`,
					body: step.body.replace(/\n/g, '<br>')
				}));

				setSelectedTemplate({
					name,
					description,
					isAiGenerated,
					steps: processedSteps
				});

				setSelectedSequence({
					name,
					status: 'DRAFT',
					steps: processedSteps,
					id: undefined,
					justCreated: true
				});

				if (screenSize.isSmaller) {
					setSelectedStep(undefined);
				} else {
					setSelectedStep(processedSteps[0]);
				}
			});
		}
	}, [
		isUserOnboarding,
		onboardingGeneratedAiSequence,
		screenSize.isSmaller,
		setSelectedSequence,
		setSelectedStep,
		setSelectedTemplate
	]);

	useEffect(() => {
		if (screenSize.isSmaller && selectedStep) {
			setSelectedStep(undefined);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [screenSize.isSmaller, setSelectedStep]);

	const goBackHomePage = () => {
		setSequencePage('MY-SEQUENCES');
		// Remove listId from the URL
		const searchParams = new URLSearchParams(window.location.search);
		searchParams.delete('listId');
		history.replace({ search: searchParams.toString() });
	};

	const handleChangeTimeToSend = (e: ListItem) => {
		if (!selectedTemplate || !selectedStep || !selectedSequence) return;

		const newStep = {
			...selectedStep,
			sendAfterInterval: Number(e.id)
		};

		const newSteps = selectedTemplate?.steps.map(step => {
			if (step.order === newStep.order) {
				return newStep;
			}
			return step;
		});

		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});

		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			sendAfter: `${newStep.sendAfterInterval} ${newStep.sendAfterType}`,
			eventName: 'user_changed_sequence_step_duration'
		});
	};

	function handleBodyChange(type: 'SUBJECT' | 'BODY', e: string) {
		if (!selectedTemplate || !selectedStep || !selectedSequence) {
			return;
		}

		const newStep = {
			...selectedStep,
			...(type === 'SUBJECT' ? { subject: e } : { body: e })
		};

		setSelectedStep(newStep);

		if (type === 'SUBJECT') {
			setSubject(e);
		}

		if (type === 'BODY') {
			setEmailBody(e);
		}

		const newSteps = selectedTemplate.steps.map(step => {
			if (step.id === selectedStep.id) {
				return newStep;
			}
			return step;
		});
		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});
	}

	const hasAiSnippet = emailBody.includes('[ai_snippet]');

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				inputSubjectRef.current &&
				!inputSubjectRef.current.contains(event.target as Node)
			) {
				setIsSubjectInitial(false);
				setIsBodyInitial(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleCustomText = (text: string) => {
		if (text === '[ai_snippet]' && hasAiSnippet) {
			handleBodyChange('BODY', emailBody.replace(text, ''));

			return;
		}

		if (focusField === 'SUBJECT' && inputSubjectRef.current) {
			const input = inputSubjectRef.current;
			const cursorPos = input.selectionStart ?? subject.length;

			// Insert text at the cursor position
			const updatedText = `${subject.slice(0, cursorPos)}[business_name]${subject.slice(cursorPos)}`;

			handleBodyChange('SUBJECT', updatedText);
		} else if (focusField === 'BODY') {
			setBodyContextMenuValue(text);
		}
	};

	useEffect(() => {
		setBodyContextMenuValue('');
	}, [bodyContextMenuValue]);

	useEffect(() => {
		if (sequencePage === 'STEPS-CREATION') {
			setEmailBody(selectedStep?.body || '');
			setSubject(selectedStep?.subject || '');
		}
	}, [selectedStep, sequencePage]);

	return (
		<div className="flex h-full flex-col gap-y-4 lg:gap-y-0 xl:pr-2">
			<HeaderRedesign
				backButton={{
					goBack: goBackHomePage,
					label: 'Back to My Sequences'
				}}
				title="Steps creation"
				tooltip="Design a sequence of up to seven steps, consisting of initial and follow-up emails. Enables you to outline and organize your email campaign in stages, from the first contact to subsequent follow-ups"
			/>

			<div className="mb-2 mt-3 flex h-full w-full items-stretch gap-2 overflow-hidden">
				<StepCreationList />
				<div
					className={cn(
						'absolute top-[1.3125rem] z-50 flex h-[calc(100vh-245px)] w-[calc(100vw-20px)] flex-grow flex-col gap-2 overflow-y-auto rounded-lg border border-dm-grey bg-dm-black p-4 lg:static lg:left-2.5 lg:h-full lg:w-auto lg:rounded-none lg:rounded-l-lg lg:py-6 xl:rounded-lg',
						screenSize.isSmaller && !selectedStep && 'hidden'
					)}
				>
					<Button
						onClick={() => setSelectedStep(undefined)}
						variant="text"
						leftIcon={<HardArrowLeft />}
						className="px-0 lg:hidden"
					>
						Back to steps
					</Button>

					<div className="my-2 flex flex-wrap gap-x-2 gap-y-4 lg:mt-0 lg:h-10 lg:items-end lg:gap-x-4">
						<AI className="text-dm-white" />

						<div className="order-1 flex h-9 items-center gap-1 text-dm-gold empty:hidden lg:order-[unset]">
							{(selectedStep?.sendAfterInterval || 0) > 0 && (
								<Dropdown
									key={selectedStep?.id}
									title={`Send after ${selectedStep?.sendAfterInterval} days`}
									headerColor="medium"
									dropdownColor="medium"
									width="100%"
									handleClick={handleChangeTimeToSend}
									list={Array.from(
										{ length: 15 },
										(_, i) => ({
											id: i + 1,
											title: `Send after ${i + 1} day${
												i > 0 ? 's' : ''
											}`
										})
									)}
									listClassName="sequence-dropdown-days"
								/>
							)}
						</div>

						<span className="flex-shrink-0 text-sm text-white">
							Click subject or email to edit
						</span>
					</div>
					<div className="sequence-scroll-bar flex h-full w-full flex-col gap-2 overflow-y-auto overflow-x-visible">
						<div className="flex flex-shrink-0 gap-6">
							<input
								ref={inputSubjectRef}
								id="subject-editor"
								type="text"
								data-is-highlighted={
									isInputsHighlighted ||
									(!onboardingModal && isSubjectInitial)
								}
								className="box-sizing-fix h-12 w-full rounded-[0.25rem] border border-dm-grey bg-dm-black3 pl-4 pr-6 text-sm text-dm-pure-white focus:bg-dm-white focus:text-dm-black data-[is-highlighted=true]:bg-dm-white data-[is-highlighted=true]:text-dm-black lg:text-base"
								value={subject}
								onFocus={() => {
									setFocusField('SUBJECT');
									setIsInputsHighlighted(true);
								}}
								onBlur={() => {
									setIsInputsHighlighted(false);
								}}
								onChange={e =>
									handleBodyChange('SUBJECT', e.target.value)
								}
							/>
						</div>
						<div className="group flex w-full min-w-0 flex-grow items-stretch overflow-auto rounded-[4px] border border-dm-grey">
							<div className="flex w-full flex-grow">
								<div
									data-is-highlighted={isInputsHighlighted}
									className="sequenceStepCreationEditor group h-full w-0 min-w-80 flex-grow"
								>
									<RichEditor
										onChange={e =>
											handleBodyChange('BODY', e)
										}
										value={emailBody}
										disableDisabledStyles
										autoFocus={
											!onboardingModal && isBodyInitial
										}
										enablePropagation
										onFocus={() => {
											setFocusField('BODY');
											setIsInputsHighlighted(true);
										}}
										onBlur={() => {
											setIsInputsHighlighted(false);
										}}
										className="h-full w-full min-w-0 flex-grow overflow-auto bg-dm-black3 !text-sm text-dm-white group-data-[is-highlighted=true]:bg-dm-white group-data-[is-highlighted=true]:text-dm-black lg:!text-base"
										contextMenuValue={bodyContextMenuValue}
										readOnly={false}
									/>
								</div>

								{!isUserOnboarding && (
									<div className="hidden flex-shrink-0 rounded-tr-[0.25rem] border-dark-grey lg:block">
										<SmartTemplates />
									</div>
								)}
							</div>
						</div>

						<Dialog.Root
							open={onboardingModal}
							onOpenChange={isOpen => {
								setOnboardingModal(isOpen);

								if (!isOpen) {
									clearAiSequenceCache();

									setTimeout(() => {
										setIsSubjectInitial(true);
										setIsBodyInitial(true);
									});
								}
							}}
						>
							<Dialog.Content className="flex max-w-[90%] flex-col items-center justify-center gap-6 !rounded-2xl p-4 lg:max-w-[35.5rem] lg:px-14 lg:py-10 [&>button]:hidden">
								<Dialog.Title className="text-2xl text-dm-gold">
									AI Campaign Ready!
								</Dialog.Title>

								<p className="text-center font-semibold text-dm-white">
									Take a moment to review the email steps
									we’ve created for you, then hit{' '}
									<span className="font-bold text-dm-gold">
										Next
									</span>
									.
								</p>

								<p className="mb-4 text-center font-semibold text-dm-white">
									On the following page, you’ll pick the
									businesses to contact. Once activated, this
									sequence campaign will automatically send
									emails and follow-ups for you.
								</p>

								<Dialog.Footer>
									<Dialog.Close>
										<Button>Got it!</Button>
									</Dialog.Close>
								</Dialog.Footer>
							</Dialog.Content>
						</Dialog.Root>

						{!isUserOnboarding && (
							<div className="hidden h-8 flex-shrink-0 items-center gap-[0.8125rem] lg:flex">
								{selectedStep?.order === 0 && (
									<Button
										variant="primary"
										smart
										red={hasAiSnippet}
										className="w-min whitespace-nowrap px-4"
										onClick={() =>
											handleCustomText('[ai_snippet]')
										}
									>
										{hasAiSnippet
											? 'Remove AI Snippet'
											: 'Add AI Snippet'}
									</Button>
								)}
								<AddCustomTextDropup
									handleCustomText={handleCustomText}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
			<NewSequenceFooter />
		</div>
	);
}

function AddCustomTextDropup({
	handleCustomText
}: {
	handleCustomText: (text: string) => void;
}) {
	const dropUpRef = useRef<HTMLDivElement>(null);
	const [isOpen, setIsOpen] = useState(false);

	useOnClickOutside({
		ref: dropUpRef,
		handler: () => setIsOpen(false)
	});

	const options = ['[business_name]'];

	const handleOpen = () => {
		setIsOpen(!isOpen);
	};

	function handleAddCustomText(text: string) {
		handleCustomText(text);
		handleOpen();
	}
	return (
		<div className="relative">
			{isOpen && (
				<div
					ref={dropUpRef}
					className="absolute bottom-9 z-10 flex w-[200px] flex-col bg-dm-black3 text-white"
				>
					{options.map(option => (
						<button
							type="button"
							key={option}
							className="w-full rounded-sm border border-dm-gray px-2 py-3 text-left hover:bg-dm-grey hover:bg-orange hover:text-dm-black"
							onClick={() => handleAddCustomText(option)}
						>
							{option}
						</button>
					))}
				</div>
			)}
			<Button
				variant="text"
				rightIcon={<BracketIcon />}
				className="px-0"
				onClick={handleOpen}
			>
				Add Custom Text
			</Button>
		</div>
	);
}

function SmartTemplates() {
	const { selectedStep } = useSequencesContext();

	if (!selectedStep?.id) return null;

	return (
		<div className="sequence-smart-templates flex h-full w-full flex-col border-dark-grey text-gray-200">
			<EmailSmartTemplates selectedTemplate={selectedStep} />
		</div>
	);
}

function StepCreationList() {
	const scrollRef = useRef<HTMLDivElement>(null);

	const {
		selectedTemplate,
		setSelectedTemplate,
		setSelectedStep,
		selectedSequence,
		setSelectedSequence
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	function handleAddStep() {
		if (!selectedTemplate || !selectedSequence) return;
		const oldSteps = selectedTemplate?.steps || [];
		const stepToAdd = {
			id: Math.random().toString(36).substring(2, 9),
			subject: '',
			body: '',
			sendAfterInterval: 3,
			sendAfterType: 'DAYS',
			order: oldSteps.length,
			justCreated: true
		};
		const newSteps = [...oldSteps, stepToAdd];

		setSelectedTemplate({
			...selectedTemplate,
			steps: newSteps
		});
		setSelectedStep(stepToAdd);
		setSelectedSequence({
			...selectedSequence,
			steps: newSteps
		});

		sendAnalytics({
			type: 'track',
			userId: user.email,
			sequenceId: selectedSequence.id,
			stepNumber: newSteps.length,
			eventName: 'user_added_a_new_sequence_step'
		});

		setTimeout(() => {
			if (scrollRef.current) {
				scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
			}
		}, 0);
	}

	const cantAddMoreSteps = (selectedTemplate?.steps || []).length >= 7;

	const stepsSorted = useMemo(() => {
		if (!selectedTemplate) return [];
		return selectedTemplate.steps.sort((a, b) => a.order - b.order);
	}, [selectedTemplate]);

	return (
		<div className="flex h-full min-w-[14.688rem] flex-shrink flex-grow basis-[22rem] flex-col overflow-hidden rounded-lg border border-dm-grey bg-dm-black lg:max-w-[39.5rem]">
			<div
				data-ai-generated={selectedTemplate?.isAiGenerated}
				className="flex flex-shrink-0 flex-col border-b border-dm-grey px-6 py-3 data-[ai-generated=true]:border-dm-blue data-[ai-generated=true]:bg-dm-blue"
			>
				<NameWithEdit />
			</div>
			<div
				ref={scrollRef}
				className="flex h-full flex-col overflow-y-auto"
			>
				{stepsSorted.map((step, index) => (
					<StepItem key={step.order} step={step} index={index} />
				))}
			</div>
			{!isUserOnboarding && (
				<button
					type="button"
					onClick={handleAddStep}
					disabled={cantAddMoreSteps}
					className="flex-shrink-1 mt-auto flex h-[4.8125rem] items-center gap-4 border-t border-dm-grey p-2 hover:bg-dm-background active:bg-dm-grey lg:p-4"
				>
					<div
						className={`flex items-center gap-4 ${
							cantAddMoreSteps ? 'text-dm-grey' : 'text-dm-gold'
						}`}
					>
						<PlusSignIcon size={9} />

						<span
							className={`text-base font-semibold lg:text-lg ${cantAddMoreSteps ? 'text-dm-grey' : 'text-white'} `}
						>
							Add a step
						</span>
					</div>
					{cantAddMoreSteps && (
						<span className="text-sm italic text-dm-orange">
							You have reached the max amount of steps
						</span>
					)}
				</button>
			)}
		</div>
	);
}

function NameWithEdit() {
	const {
		selectedTemplate,
		selectedSequence,
		setSelectedSequence,
		sequences,
		setSequences
	} = useSequencesContext();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	const [isEditing, setIsEditing] = useState(false);
	const [name, setName] = useState(selectedSequence?.name || 'New Sequence');

	function handleIsEditing() {
		setIsEditing(!isEditing);
	}

	function handleCancelEdit() {
		setIsEditing(false);
		setName(selectedSequence?.name || 'New Sequence');
	}

	function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setIsEditing(false);
		if (!selectedSequence || !selectedTemplate) return;

		if (!selectedSequence.id) {
			// Sequence doest exist yet
			setSelectedSequence({
				...selectedSequence,
				name
			});
			return;
		}

		// Sequence already exists
		updateSequence({
			campaignId: selectedSequence.id,
			name
		})
			.then(res => {
				if (res.status !== 200) {
					throw new Error(
						'Error editing sequence name! Please try again.'
					);
				}
				setSequences(
					sequences?.map(c => {
						if (c.id === selectedSequence.id) {
							return {
								...c,
								name
							};
						}
						return c;
					})
				);
				setSelectedSequence({
					...selectedSequence,
					name
				});

				sendAnalytics({
					type: 'track',
					userId: user.email,
					sequenceId: selectedSequence.id,
					eventName: 'user_edited_sequence_name'
				});
			})
			.catch(err => {
				toast.error(err.message);
				setName(selectedSequence?.name || 'New Sequence');
			});
	}

	return (
		<div className="">
			<div className="flex flex-col items-start gap-x-5 gap-y-3 xxl:flex-row xxl:flex-wrap xxl:items-start">
				{isEditing ? (
					<div className="flex w-full flex-col gap-y-0.5">
						<form
							className="flex w-full gap-2"
							onSubmit={handleFormSubmit}
						>
							<input
								autoFocus
								type="text"
								className="h-7 w-full -translate-x-2 bg-gray-200 py-0 pl-2 pr-0 text-sm font-medium text-gray-800 lg:text-lg"
								placeholder="Name your template here"
								value={name}
								onChange={e => setName(e.target.value)}
							/>
							<div className="m-0 flex flex-shrink-0 items-center gap-4 text-dm-pure-white">
								<button
									id="campaigns-rename-checkmark"
									type="submit"
								>
									<Checkmark width={20} />
								</button>
								<button
									onClick={handleCancelEdit}
									type="button"
								>
									<CloseThin width={14} />
								</button>
							</div>
						</form>

						<span className="text-sm text-dm-mid-grey">
							{pluralize(
								'Step',
								selectedTemplate?.steps.length,
								true
							)}
						</span>
					</div>
				) : (
					<div className="flex w-full flex-col gap-y-0.5 lg:w-auto">
						<div className="flex items-center gap-5">
							<div className="flex min-w-0 flex-grow items-center gap-1">
								{selectedTemplate?.isAiGenerated && (
									<AI className="mr-0 flex-shrink-0 text-white" />
								)}

								<span className="line-clamp-1 text-sm font-semibold text-dm-gold lg:text-lg">
									{selectedSequence?.name}
								</span>
							</div>

							<button
								type="button"
								onClick={handleIsEditing}
								className="m-0 h-6 w-6 flex-shrink-0 text-dm-white"
							>
								<PencilEdit />
							</button>
						</div>

						<span className="text-sm text-dm-mid-grey">
							{pluralize(
								'Step',
								selectedTemplate?.steps.length,
								true
							)}
						</span>
					</div>
				)}
				{selectedTemplate?.isAiGenerated && !isUserOnboarding && (
					<AiSequenceDialogForm
						trigger={
							<Button
								card
								smart
								red
								className="min-w-max flex-shrink-0"
							>
								Review and edit my prompt
							</Button>
						}
					/>
				)}
			</div>
		</div>
	);
}

function StepItem({ step, index }: { step: StepsType; index: number }) {
	const { selectedStep, setSelectedStep } = useSequencesContext();
	const screenSize = useIsScreenWidthSmallerThan(1024);

	const { stepDropdownMenuOptions } = useSteps(step);

	const isSelected = step.id === selectedStep?.id;

	function handleSelectStep() {
		if (screenSize.isSmaller && selectedStep?.id === step.id) {
			setSelectedStep(undefined);
		} else {
			setSelectedStep(step);
		}
	}

	return (
		<div
			role="button"
			tabIndex={0}
			onKeyDown={e => {
				if (e.key === 'Enter') {
					handleSelectStep();
				}
			}}
			onClick={handleSelectStep}
			className={`flex h-[4.8125rem] flex-shrink-0 cursor-pointer items-center gap-4 border-b border-dashed border-dm-gold px-4 py-4 hover:bg-dm-black3 lg:pr-8 ${isSelected ? 'bg-dm-black3' : 'border-opacity-20'} `}
		>
			<div className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
				<CircleNumber
					number={index + 1}
					variant={isSelected ? 'filled' : 'outlined'}
				/>
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<div className="flex gap-4 overflow-hidden text-sm text-white lg:text-lg">
					<span className="whitespace-nowrap">
						{index === 0
							? 'Initial'
							: `After ${step.sendAfterInterval} Day${
									step.sendAfterInterval > 1 ? 's' : ''
								}`}
					</span>
					<span className="truncate font-semibold">
						{step.subject}
					</span>
				</div>
			</div>
			<div className="ml-auto flex-shrink-0 rounded-lg hover:bg-dark-grey hover:bg-opacity-20 [&>*>*>*>svg]:rotate-90 [&>*>*>*>svg]:lg:rotate-0">
				<DropdownMenu
					options={stepDropdownMenuOptions}
					itemClassName="p-2 hover:bg-dm-black3"
					useFixedPosition={screenSize.isSmaller}
				>
					<HorizontalDotsButton />
				</DropdownMenu>
			</div>
		</div>
	);
}
